"use client";

import { usePathname, useRouter } from "next/navigation";
import React, { useState } from "react";
import { Components } from "@/app/(components)/_components/Components";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconArrowLeft from "@/app/(components)/generics/Icons/IconArrowLeft";
import IconArrowRight from "@/app/(components)/generics/Icons/IconArrowRight";
import IconArrowUp from "@/app/(components)/generics/Icons/IconArrowUp";
import IconEnterprise from "@/app/(components)/generics/Icons/IconEnterprise";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import PageLoader from "@/app/(components)/generics/loaders/PageLoader/PageLoader";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import SiteFavicon from "@/app/(components)/generics/SiteFavicon/SiteFavicon";
import { Tooltip, TooltipPosition, TooltipType } from "@/app/(components)/generics/Tootlip/Tooltip";
import { useCanManageWorkspace } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { ModalStore } from "@/app/stores/ModalStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PageParams } from "@/app/types/common/DefaultProps";
import { ProjectLite } from "@/app/types/project/ProjectLite";
import { URLs } from "@/app/utils/URLs";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import { ProtectedPaths, Routes } from "@/app/vars/Routes";
import sidebarStyles from "../../Sidebar.module.css";
import styles from "./SidebarProjects.module.css";

type Props = {
  params: PageParams;
  isOpen: boolean;
};

export default function SidebarProjects(props: Props) {
  const T = useT();
  const { params, isOpen } = props;
  const { lang } = params;

  const { currentProject: project, projects, setCurrentProject } = ProjectStore();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  const canAddProject = useCanManageWorkspace();

  if (!project) return null;

  const isActive = pathname.includes(new URL(Routes.PROTECTED.PROJECT(project.uuid)).pathname);

  const handleOpenSettings = (e: React.MouseEvent) => {
    e.stopPropagation();
    router.push(URLs.pathOf(Routes.PROTECTED.PROJECT(project.uuid)));
  };

  const handleSelectProject = (p: ProjectLite) => {
    if (p.id === project.id) {
      setIsDropdownOpen(false);
      return;
    }

    setCurrentProject(p);
    setIsDropdownOpen(false);

    setIsSwitching(true);

    // A project page shows one project by its uuid: reloading it would land back on the
    // project we just left.
    const target = pathname.includes(ProtectedPaths.PROJECT)
      ? URLs.pathOf(Routes.PROTECTED.PROJECT(p.uuid))
      : null;

    setTimeout(() => (target ? window.location.assign(target) : window.location.reload()), 100);
  };

  const handleAddProject = async (e: React.MouseEvent) => {
    e.stopPropagation();
    ModalStore.getState().openModal(Components.ProjectForm, T(TKey.PROJECT_ADD), {
      lang,
      params,
      isCreating: true,
    });
  };

  const hasMultipleProjects = projects.length > 1;

  return (
    <>
      {isSwitching && <PageLoader active={true} delay={0} />}

      <div className={UStyles.classNames(styles.wrapper, !isOpen ? styles.collapsedWrapper : "")}>
        {!isOpen ? (
          <div className={styles.collapsedContent}>
            <div className={styles.fullWidthTrigger}>
              <Tooltip
                position={TooltipPosition.RIGHT}
                gap={Limits.SIDEBAR_TOOLTIP_GAP}
                type={TooltipType.GREEN}
                content={project.name}
                className={sidebarStyles.fullWidth}
                disabled={isDropdownOpen}
                trigger={
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <div className={UStyles.classNames(styles.container, styles.collapsed)}>
                      <div className={styles.iconContainer} onClick={handleOpenSettings}>
                        {project.link ? (
                          <SiteFavicon link={project.link} className={styles.favicon} />
                        ) : (
                          <IconEnterprise />
                        )}
                      </div>

                      {hasMultipleProjects && (
                        <Popover
                          isVisible={isDropdownOpen}
                          setIsVisible={setIsDropdownOpen}
                          openAfterClick={true}
                          closeWhenClickOutside={true}
                          isPortaled={false}
                          className={UStyles.classNames(styles.popover, styles.popoverCollapsed)}
                        >
                          <PopoverTrigger
                            className={UStyles.classNames(
                              styles.arrowContainer,
                              isDropdownOpen ? styles.arrowActive : "",
                            )}
                          >
                            {isDropdownOpen ? (
                              <IconArrowLeft size={14} />
                            ) : (
                              <IconArrowRight size={14} />
                            )}
                          </PopoverTrigger>
                          <PopoverContent
                            position={{
                              default: {
                                top: "0",
                                left: `calc(100% + ${Limits.SIDEBAR_TOOLTIP_GAP * 2}px)`,
                              },
                            }}
                            className={styles.popoverContent}
                          >
                            <div className={styles.dropdown}>
                              <div className={styles.projectList}>
                                {projects.map((p) => (
                                  <div
                                    key={p.id}
                                    className={UStyles.classNames(
                                      styles.dropdownItem,
                                      p.id === project.id ? styles.activeProject : "",
                                    )}
                                    onClick={() => handleSelectProject(p)}
                                  >
                                    <div className={styles.dropdownIcon}>
                                      {p.link ? (
                                        <SiteFavicon
                                          link={p.link}
                                          className={styles.faviconSmall}
                                        />
                                      ) : (
                                        <IconEnterprise />
                                      )}
                                    </div>
                                    <span className={styles.dropdownName}>{p.name}</span>
                                  </div>
                                ))}
                              </div>
                              {canAddProject && (
                                <>
                                  <div className={styles.dropdownSeparator} />
                                  <Button
                                    full={true}
                                    min={true}
                                    isLink={false}
                                    type={ButtonType.PRIMARY}
                                    text={T(TKey.PROJECT_ADD) as string}
                                    beforeIcon={<IconPlus size={14} />}
                                    onClick={(e: React.MouseEvent) => {
                                      setIsDropdownOpen(false);
                                      handleAddProject(e);
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </div>
                }
              />
            </div>
          </div>
        ) : (
          <div className={styles.container}>
            <div className={styles.projectMain}>
              <div className={styles.projectClickArea} onClick={handleOpenSettings}>
                <div className={styles.iconContainer}>
                  {project.link ? (
                    <SiteFavicon link={project.link} className={styles.favicon} />
                  ) : (
                    <IconEnterprise />
                  )}
                </div>
                <div className={styles.info}>
                  <span
                    className={UStyles.classNames(styles.name, isActive ? styles.nameActive : "")}
                  >
                    {project.name}
                  </span>
                </div>
              </div>

              {hasMultipleProjects && (
                <Popover
                  isVisible={isDropdownOpen}
                  setIsVisible={setIsDropdownOpen}
                  openAfterClick={true}
                  closeWhenClickOutside={true}
                  isPortaled={true}
                  className={styles.popover}
                >
                  <PopoverTrigger
                    className={UStyles.classNames(
                      styles.arrowContainer,
                      isDropdownOpen ? styles.arrowActive : "",
                    )}
                  >
                    {isDropdownOpen ? <IconArrowUp size={21} /> : <IconArrowDown size={21} />}
                  </PopoverTrigger>
                  <PopoverContent
                    position={{ default: { top: "calc(100% + 4px)", left: "0" } }}
                    className={styles.popoverContent}
                  >
                    <div className={styles.dropdown}>
                      <div className={styles.projectList}>
                        {projects.map((p) => (
                          <div
                            key={p.id}
                            className={UStyles.classNames(
                              styles.dropdownItem,
                              p.id === project.id ? styles.activeProject : "",
                            )}
                            onClick={() => handleSelectProject(p)}
                          >
                            <div className={styles.dropdownIcon}>
                              {p.link ? (
                                <SiteFavicon link={p.link} className={styles.faviconSmall} />
                              ) : (
                                <IconEnterprise />
                              )}
                            </div>
                            <span className={styles.dropdownName}>{p.name}</span>
                          </div>
                        ))}
                      </div>
                      {canAddProject && (
                        <>
                          <div className={styles.dropdownSeparator} />
                          <Button
                            full={true}
                            min={true}
                            isLink={false}
                            type={ButtonType.PRIMARY}
                            text={T(TKey.PROJECT_ADD) as string}
                            beforeIcon={<IconPlus size={14} />}
                            onClick={(e: React.MouseEvent) => {
                              setIsDropdownOpen(false);
                              handleAddProject(e);
                            }}
                          />
                        </>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              {!hasMultipleProjects && canAddProject && (
                <button className={styles.addBtn} onClick={handleAddProject}>
                  <IconPlus size={14} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
